import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Gift, Globe, MapPin, Send, ShieldAlert, Star, TrendingUp } from "lucide-react";
import { DetailSkeleton } from "@/components/marketplace";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/contexts/AuthContext";
import { useCreatorByHandle, type SocialAccount } from "@/hooks/useMarketplace";
import { PLATFORM_LABELS, countryLabel, formatFollowers } from "./constants";
import InviteToCampaignDialog from "./components/InviteToCampaignDialog";
import {
  CreatorAvatar,
  ProvenanceBadge,
  displayHandle,
  provenanceOf,
  rateLabel,
} from "./CreatorPresentation";

function AccountRow({ account }: { account: SocialAccount }) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-neutral-100 last:border-0">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-semibold text-neutral-900">
            {PLATFORM_LABELS[account.platform]}
          </p>
          <ProvenanceBadge account={account} t={t} />
        </div>
        <p className="text-xs text-neutral-400 truncate">@{account.handle}</p>
      </div>

      <div className="flex items-center gap-6 text-right flex-shrink-0">
        <div>
          <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-widest">
            {t("marketplace.creator.followers")}
          </p>
          <p className="text-sm font-bold text-neutral-900">
            {formatFollowers(account.followers)}
          </p>
        </div>
        {account.engagementRate !== null && (
          <div>
            <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-widest">
              {t("marketplace.creator.engagement")}
            </p>
            <p className="text-sm font-bold text-neutral-900">
              {account.engagementRate.toFixed(1)}%
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CreatorProfilePage() {
  const { handle } = useParams<{ handle: string }>();
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const { t } = useTranslation();
  const { user } = useAuth();
  const { creator, loading } = useCreatorByHandle(handle);

  if (loading) {
    return (
      <div className="adv-page">
        <DetailSkeleton />
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <p className="text-sm font-medium text-neutral-900">
          {t("marketplace.creator.notFoundTitle")}
        </p>
        <p className="text-sm text-neutral-500 mt-1">{t("marketplace.creator.notFoundBody")}</p>
        <Link
          to="/descubrir"
          className="inline-block mt-4 text-sm font-medium text-steel-600 hover:text-steel-700"
        >
          {t("marketplace.creator.backToDiscover")}
        </Link>
      </div>
    );
  }

  const rate = rateLabel(creator, t);
  const selfReportedCount = creator.socialAccounts.filter(
    (a) => provenanceOf(a) === "self",
  ).length;
  const publicCount = creator.socialAccounts.filter((a) => provenanceOf(a) === "public").length;

  // Brands only, and only for a claimed profile: an invitation to a discovered
  // profile is a letter to an address nobody reads, since there is no account
  // behind it to receive one. The page itself stays public — this is the shop
  // window, and a login wall on it would cost more than the button earns.
  const canInvite = user?.marketplaceRole === "BRAND" && creator.source === "SELF_REGISTERED";

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <Link
        to="/descubrir"
        className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        {t("marketplace.creator.backToDiscover")}
      </Link>

      <div className="bg-white border border-neutral-200 rounded-card p-6">
        <div className="flex items-start gap-4">
          <CreatorAvatar
            creator={creator}
            size={64}
            failed={avatarFailed}
            onFail={() => setAvatarFailed(true)}
          />

          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold text-neutral-900">{creator.displayName}</h1>
            <p className="text-sm text-neutral-400">@{displayHandle(creator)}</p>

            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-neutral-500">
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {creator.city ? `${creator.city}, ` : ""}
                {countryLabel(creator.country, t("marketplace.creator.unknownCountry"))}
              </span>
              {creator.languages.length > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Globe className="w-3.5 h-3.5" />
                  {creator.languages.join(", ").toUpperCase()}
                </span>
              )}
              {creator.avgRating !== null && (
                <span className="inline-flex items-center gap-1">
                  <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                  {creator.avgRating.toFixed(1)}
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <TrendingUp className="w-3.5 h-3.5" />
                {t("marketplace.creator.collaborations", {
                  count: creator.totalCollaborations,
                })}
              </span>
            </div>
          </div>

          <div className="text-right flex-shrink-0">
            <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-widest">
              {t("marketplace.creator.fromRate")}
            </p>
            <p className={rate.known ? "text-lg font-bold text-neutral-900" : "text-sm text-neutral-400"}>
              {rate.text}
            </p>
            {creator.acceptsGifting && creator.source === "SELF_REGISTERED" && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 mt-0.5">
                <Gift className="w-3 h-3" />
                {t("marketplace.creator.gifting")}
              </span>
            )}

            {canInvite && (
              <button
                type="button"
                onClick={() => setInviteOpen(true)}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-button text-white bg-gradient-to-r from-steel-600 to-steel-700 hover:opacity-90 transition-opacity cursor-pointer"
              >
                <Send className="w-3.5 h-3.5" />
                {t("marketplace.invite.cta")}
              </button>
            )}
          </div>
        </div>

        {creator.bio && (
          <p className="text-sm text-neutral-600 leading-relaxed mt-4">{creator.bio}</p>
        )}

        {/* A discovered profile belongs to someone who has never used the app.
            Saying so prevents a brand assuming they can be contacted here. */}
        {creator.source !== "SELF_REGISTERED" && (
          <p className="flex items-start gap-1.5 text-xs text-neutral-500 mt-4 px-3 py-2 rounded-control bg-neutral-50 border border-neutral-200">
            <Globe className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
            {t("marketplace.creator.unclaimedNotice")}
          </p>
        )}

        {creator.niches.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-4">
            {creator.niches.map((n) => (
              <span
                key={n}
                className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-neutral-100 text-neutral-600"
              >
                {t(`marketplace.niches.${n}`, { defaultValue: n })}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-neutral-200 rounded-card p-6 mt-4">
        <h2 className="text-sm font-semibold text-neutral-900 mb-1">
          {t("marketplace.creator.audienceTitle")}
        </h2>

        {selfReportedCount > 0 && (
          <p className="flex items-start gap-1.5 text-xs text-amber-700 mb-3">
            <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
            {t("marketplace.creator.unverifiedNotice", { count: selfReportedCount })}
          </p>
        )}
        {publicCount > 0 && selfReportedCount === 0 && (
          <p className="flex items-start gap-1.5 text-xs text-neutral-500 mb-3">
            <Globe className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
            {t("marketplace.creator.publicDataNotice")}
          </p>
        )}

        <div className="mt-2">
          {creator.socialAccounts.map((a) => (
            <AccountRow key={a.id} account={a} />
          ))}
        </div>
      </div>

      {canInvite && (
        <InviteToCampaignDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          creatorProfileId={creator.id}
          creatorName={creator.displayName}
        />
      )}
    </div>
  );
}
